/**
 * The seam between chat's UI state and the address bar.
 *
 * WHY THIS IS INJECTED RATHER THAN READ DIRECTLY
 *
 * ChatWidget is not only a page. It is also a React component embedded inside
 * other apps (apps/benchmarks renders it as a floating panel) and a shadow-DOM
 * web component that runs on pages we do not control. If the widget wrote to
 * `window.location` itself, mounting it would silently rewrite its host page's
 * URL and push entries into that page's history — a component hijacking the
 * address bar of the app embedding it.
 *
 * So the capability is handed in. `apps/chat` supplies a Next-based adapter and
 * gets real URLs; every other consumer passes nothing and behaves exactly as it
 * did before. The absence of an adapter is a supported, silent no-op, not a
 * degraded mode.
 *
 * WHAT LIVES IN THE URL
 *
 * A conversation (`/conversation/<id>`), which panel is open (`?panel=`), a
 * message to scroll to (`#msg-<id>`), and the persona/model a link wants
 * preselected (`?persona=`, `?model=`). Everything else — drafts, scroll
 * position, in-flight state — stays in memory, because a URL is something a
 * person shares and returns to, not a serialisation of the whole app.
 */

/** Panels that can be opened by link. */
export type RoutablePanel =
  | 'settings'
  | 'tools'
  | 'history'
  /**
   * The context/delete-messages dialog.
   *
   * Deep-linkable so a conversation can be handed over with "you need to trim
   * this" already on screen. It is destructive but not IRREVERSIBLE from a
   * link alone: opening it only shows the dialog, and every action inside it
   * still needs its own click. A link that deleted something on load would be
   * a different thing entirely, and is not what this does.
   */
  | 'context'
  | 'private';

export const ROUTABLE_PANELS: readonly RoutablePanel[] = [
  'settings',
  'tools',
  'history',
  'context',
  'private',
];

export function isRoutablePanel(value: string | null | undefined): value is RoutablePanel {
  return !!value && (ROUTABLE_PANELS as readonly string[]).includes(value);
}

/**
 * The app state a URL can express, already parsed.
 *
 * Every field is optional: a bare `/` is a valid, complete state meaning "the
 * most recent conversation, nothing open".
 */
export interface ChatRoute {
  /** Session id from `/conversation/<id>`. Absent on `/`. */
  conversationId?: string;
  /** Panel from `?panel=`. */
  panel?: RoutablePanel;
  /** Message id from `#msg-<id>`, to scroll to and highlight. */
  messageId?: string;
  /** Persona to preselect, from `?persona=`. */
  personaId?: string;
  /** Model to preselect, from `?model=`. */
  model?: string;
}

/**
 * What a host must provide for chat to use the address bar.
 *
 * Deliberately tiny and framework-free: read the current route, subscribe to
 * external changes (back/forward), and request a new one. Anything richer would
 * bind the widget to one router's semantics.
 */
export interface ChatRouting {
  /** The route as it is right now. */
  read(): ChatRoute;
  /**
   * Ask the host to show `route`.
   *
   * `replace` swaps the current history entry instead of pushing one. Used for
   * changes the user did not navigate to — syncing the id of a conversation
   * they were already in should not put a new entry in their back button.
   */
  navigate(route: ChatRoute, opts?: { replace?: boolean }): void;
  /**
   * Observe route changes the app did not initiate (back/forward, a pasted
   * URL). Returns an unsubscribe.
   */
  subscribe(onChange: (route: ChatRoute) => void): () => void;
}

// ── URL <-> ChatRoute, shared by any adapter ────────────────────────────────
//
// Kept here rather than in the Next adapter so the parsing has one definition,
// and so a non-Next host (or a test) does not have to reimplement the format.

/** The path prefix a conversation lives under. */
export const CONVERSATION_PREFIX = '/conversation';

/** `#msg-<id>` — prefixed so the fragment cannot collide with a plain anchor. */
export const MESSAGE_HASH_PREFIX = 'msg-';

/** Build the DOM id for a message, so links and elements cannot drift apart. */
export function messageElementId(messageId: string): string {
  return `${MESSAGE_HASH_PREFIX}${messageId}`;
}

/**
 * Parse a location into a route.
 *
 * Tolerant by design: an unrecognised panel, a stray query param or a malformed
 * path yields a route missing that field rather than throwing. A bad URL should
 * land the user in the app, not on an error.
 */
export function parseRoute(pathname: string, search: string, hash: string): ChatRoute {
  const route: ChatRoute = {};

  if (pathname.startsWith(`${CONVERSATION_PREFIX}/`)) {
    const id = decodeURIComponent(pathname.slice(CONVERSATION_PREFIX.length + 1)).replace(/\/+$/, '');
    if (id) route.conversationId = id;
  }

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const panel = params.get('panel');
  if (isRoutablePanel(panel)) route.panel = panel;
  const persona = params.get('persona');
  if (persona) route.personaId = persona;
  const model = params.get('model');
  if (model) route.model = model;

  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw.startsWith(MESSAGE_HASH_PREFIX)) {
    const id = decodeURIComponent(raw.slice(MESSAGE_HASH_PREFIX.length));
    if (id) route.messageId = id;
  }

  return route;
}

/** Serialise a route back to a root-relative URL. */
export function buildHref(route: ChatRoute): string {
  const path = route.conversationId
    ? `${CONVERSATION_PREFIX}/${encodeURIComponent(route.conversationId)}`
    : '/';

  const params = new URLSearchParams();
  if (route.panel) params.set('panel', route.panel);
  if (route.personaId) params.set('persona', route.personaId);
  if (route.model) params.set('model', route.model);
  const query = params.toString();

  const hash = route.messageId ? `#${messageElementId(route.messageId)}` : '';
  return `${path}${query ? `?${query}` : ''}${hash}`;
}
